class AnanasProduct extends HTMLElement {
  connectedCallback() {
    this.mediaItems = [...this.querySelectorAll('[data-product-media]')];
    this.dots = [...this.querySelectorAll('[data-product-dot]')];
    this.galleryStack = this.querySelector('[data-product-media-stack]');
    this.dotsContainer = this.querySelector('[data-product-dots]');
    this.optionGroups = [...this.querySelectorAll('[data-option-group]')];
    this.variantInput = this.querySelector('[data-variant-id]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.priceNode = this.querySelector('[data-product-price]');
    this.descriptionNode = this.querySelector('[data-product-description]');
    this.drawer = this.querySelector('[data-product-drawer]');
    this.drawerTitle = this.querySelector('[data-product-drawer-title]');
    this.drawerContent = this.querySelector('[data-product-drawer-content]');
    this.lightbox = this.querySelector('[data-product-lightbox]');
    this.lightboxImage = this.querySelector('[data-product-lightbox-image]');
    this.variantData = this.readVariants();
    this.defaultMedia = this.readRenderedMedia();
    this.currentMediaIndex = this.mediaItems.findIndex((media) => media.hasAttribute('data-active'));
    if (this.currentMediaIndex < 0) this.currentMediaIndex = 0;
    this.dragState = null;
    this.suppressMediaClick = false;
    this.scrollFrame = null;
    this.lastGalleryWidth = this.galleryStack?.clientWidth || 0;

    this.bindGalleryEvents();
    this.querySelectorAll('[data-option-button]').forEach((button) => {
      button.addEventListener('click', this.onOptionClick);
    });
    this.querySelectorAll('[data-product-drawer-trigger]').forEach((button) => {
      button.addEventListener('click', this.onDrawerTrigger);
    });
    this.querySelectorAll('[data-product-drawer-close]').forEach((button) => {
      button.addEventListener('click', this.closeDrawer);
    });
    this.querySelectorAll('[data-product-lightbox-close]').forEach((button) => {
      button.addEventListener('click', this.closeLightbox);
    });
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);
    this.updateVariant();
    if (!this.variantData.length) {
      this.setMedia(this.currentMediaIndex, false);
    }
  }

  disconnectedCallback() {
    this.unbindGalleryEvents();
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    this.querySelectorAll('[data-option-button]').forEach((button) => {
      button.removeEventListener('click', this.onOptionClick);
    });
    this.querySelectorAll('[data-product-drawer-trigger]').forEach((button) => {
      button.removeEventListener('click', this.onDrawerTrigger);
    });
    this.querySelectorAll('[data-product-drawer-close]').forEach((button) => {
      button.removeEventListener('click', this.closeDrawer);
    });
    this.querySelectorAll('[data-product-lightbox-close]').forEach((button) => {
      button.removeEventListener('click', this.closeLightbox);
    });
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
  }

  readVariants() {
    const script = this.querySelector('[data-product-variants]');
    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  bindGalleryEvents() {
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
    this.mediaItems.forEach((media) => media.addEventListener('click', this.onMediaClick));

    if (!this.galleryStack || this.mediaItems.length <= 1) return;

    this.galleryStack.addEventListener('scroll', this.onGalleryScroll, { passive: true });
    this.galleryStack.addEventListener('pointerdown', this.onGalleryPointerDown);
    this.galleryStack.addEventListener('pointermove', this.onGalleryPointerMove);
    this.galleryStack.addEventListener('pointerup', this.onGalleryPointerUp);
    this.galleryStack.addEventListener('pointercancel', this.onGalleryPointerCancel);
    this.galleryStack.addEventListener('lostpointercapture', this.onGalleryPointerCancel);
  }

  unbindGalleryEvents() {
    this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    this.mediaItems.forEach((media) => media.removeEventListener('click', this.onMediaClick));

    if (!this.galleryStack) return;

    this.galleryStack.removeEventListener('scroll', this.onGalleryScroll);
    this.galleryStack.removeEventListener('pointerdown', this.onGalleryPointerDown);
    this.galleryStack.removeEventListener('pointermove', this.onGalleryPointerMove);
    this.galleryStack.removeEventListener('pointerup', this.onGalleryPointerUp);
    this.galleryStack.removeEventListener('pointercancel', this.onGalleryPointerCancel);
    this.galleryStack.removeEventListener('lostpointercapture', this.onGalleryPointerCancel);
  }

  readRenderedMedia() {
    return this.mediaItems
      .map((media) => {
        const image = media.querySelector('img');
        return {
          src: image?.currentSrc || image?.src || '',
          largeSrc: media.dataset.mediaLargeSrc || image?.currentSrc || image?.src || '',
          alt: media.dataset.mediaAlt || image?.alt || '',
        };
      })
      .filter((media) => media.src);
  }

  escapeAttribute(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('"', '&quot;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }

  renderGallery(mediaItems = []) {
    if (!this.galleryStack) return;

    const nextMedia = Array.isArray(mediaItems) && mediaItems.length ? mediaItems : this.defaultMedia;
    if (!nextMedia.length) return;

    this.unbindGalleryEvents();

    this.galleryStack.innerHTML = nextMedia.map((media, index) => {
      const src = this.escapeAttribute(media.src);
      const largeSrc = this.escapeAttribute(media.largeSrc || media.src);
      const alt = this.escapeAttribute(media.alt || '');
      const loading = index === 0 ? 'eager' : 'lazy';

      return `
        <figure
          class="ananas-product__media"
          data-product-media
          data-media-index="${index}"
          data-media-large-src="${largeSrc}"
          data-media-alt="${alt}"
          data-ananas-lightbox-trigger
          data-ananas-lightbox-src="${largeSrc}"
          data-ananas-lightbox-alt="${alt}"
          ${index === 0 ? 'data-active' : ''}
        >
          <img src="${src}" alt="${alt}" loading="${loading}" sizes="(min-width: 990px) 48vw, 94vw">
        </figure>
      `;
    }).join('');

    if (this.dotsContainer) {
      this.dotsContainer.hidden = nextMedia.length <= 1;
      this.dotsContainer.innerHTML = nextMedia.length > 1
        ? nextMedia.map((_, index) => `
          <button
            type="button"
            class="ananas-product__dot"
            data-product-dot
            data-media-index="${index}"
            aria-label="${index + 1}. görsel"
            aria-current="${index === 0 ? 'true' : 'false'}"
            ${index === 0 ? 'data-active' : ''}
          ></button>
        `).join('')
        : '';
    }

    this.mediaItems = [...this.querySelectorAll('[data-product-media]')];
    this.dots = [...this.querySelectorAll('[data-product-dot]')];
    this.currentMediaIndex = 0;
    this.bindGalleryEvents();
    this.setMedia(0, false);
  }

  onDotClick = (event) => {
    const dot = event.currentTarget;
    if (!(dot instanceof HTMLElement)) return;
    this.setMedia(Number(dot.dataset.mediaIndex || 0));
  };

  setMedia(index, animate = true) {
    const nextIndex = this.updateActiveMedia(index);

    if (this.galleryStack) {
      this.galleryStack.scrollTo({
        left: Math.round(this.galleryStack.clientWidth * nextIndex),
        behavior: animate ? 'smooth' : 'auto',
      });
    }
  }

  updateActiveMedia(index) {
    const nextIndex = Math.max(0, Math.min(index, this.mediaItems.length - 1));
    this.currentMediaIndex = nextIndex;

    this.mediaItems.forEach((media, mediaIndex) => {
      media.toggleAttribute('data-active', mediaIndex === nextIndex);
    });
    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === nextIndex;
      dot.toggleAttribute('data-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    return nextIndex;
  }

  onGalleryScroll = () => {
    if (this.scrollFrame || !this.galleryStack) return;

    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (!this.galleryStack) return;

      const slideWidth = Math.max(1, this.galleryStack.clientWidth);
      const nearestIndex = Math.round(this.galleryStack.scrollLeft / slideWidth);
      if (nearestIndex !== this.currentMediaIndex) this.updateActiveMedia(nearestIndex);
    });
  };

  moveMedia(direction) {
    if (this.mediaItems.length <= 1) return;

    const nextIndex = (this.currentMediaIndex + direction + this.mediaItems.length) % this.mediaItems.length;
    this.setMedia(nextIndex);
  }

  onGalleryPointerDown = (event) => {
    // Touch and pen use native scroll + scroll-snap; JS drag is only for mouse.
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this.galleryStack) return;

    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: this.galleryStack.scrollLeft,
      startIndex: this.currentMediaIndex,
      deltaX: 0,
      isDragging: false,
    };
    this.galleryStack.setPointerCapture(event.pointerId);
  };

  onGalleryPointerMove = (event) => {
    if (!this.dragState || !this.galleryStack || event.pointerId !== this.dragState.pointerId) return;

    const deltaX = event.clientX - this.dragState.startX;
    const deltaY = event.clientY - this.dragState.startY;

    if (!this.dragState.isDragging && Math.abs(deltaX) < 8) return;
    if (!this.dragState.isDragging && Math.abs(deltaY) > Math.abs(deltaX)) return;

    this.dragState.isDragging = true;
    this.dragState.deltaX = deltaX;
    this.galleryStack.toggleAttribute('data-dragging', true);
    this.galleryStack.scrollLeft = this.dragState.startScrollLeft - deltaX;
    event.preventDefault();
  };

  onGalleryPointerUp = (event) => {
    if (!this.dragState || !this.galleryStack || event.pointerId !== this.dragState.pointerId) return;

    const { deltaX, isDragging, startIndex } = this.dragState;

    if (!isDragging) {
      this.endGalleryDrag();
      return;
    }

    this.suppressMediaClick = true;
    window.setTimeout(() => {
      this.suppressMediaClick = false;
    }, 140);

    const slideWidth = Math.max(1, this.galleryStack.clientWidth);
    const nearestIndex = Math.round(this.galleryStack.scrollLeft / slideWidth);
    const swipeThreshold = Math.min(34, slideWidth * 0.1);
    const nextIndex = Math.abs(deltaX) >= swipeThreshold
      ? startIndex + (deltaX < 0 ? 1 : -1)
      : nearestIndex;

    this.setMedia(nextIndex);
    this.endGalleryDrag();
  };

  onGalleryPointerCancel = () => {
    if (this.dragState?.isDragging) {
      this.setMedia(this.currentMediaIndex);
    }
    this.endGalleryDrag();
  };

  endGalleryDrag() {
    this.galleryStack?.removeAttribute('data-dragging');
    this.dragState = null;
  }

  onMediaClick = (event) => {
    if (this.dragState?.isDragging || this.suppressMediaClick) return;

    const media = event.currentTarget;
    if (!(media instanceof HTMLElement)) return;

    const mediaIndex = Number(media.dataset.mediaIndex || this.currentMediaIndex || 0);
    this.setMedia(mediaIndex);
    this.openLightbox(media);
  };

  openLightbox(media) {
    if (window.AnanasLightbox) {
      window.AnanasLightbox.open(media);
      return;
    }

    if (!this.lightbox || !this.lightboxImage) return;

    const image = media.querySelector('img');
    const imageSrc = media.dataset.mediaLargeSrc || image?.currentSrc || image?.src || '';
    if (!imageSrc) return;

    this.lightboxImage.src = imageSrc;
    this.lightboxImage.alt = media.dataset.mediaAlt || image?.alt || '';
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ananas-product-lightbox-open');
    this.lightbox.querySelector('[data-product-lightbox-close]')?.focus();
  }

  closeLightbox = () => {
    if (!this.lightbox || !this.lightboxImage) return;

    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ananas-product-lightbox-open');
    this.lightboxImage.removeAttribute('src');
    this.lightboxImage.alt = '';
  };

  onResize = () => {
    // Mobile browsers fire resize when the URL bar shows/hides; only re-align on real width changes.
    const width = this.galleryStack?.clientWidth || 0;
    if (width === this.lastGalleryWidth) return;
    this.lastGalleryWidth = width;
    this.setMedia(this.currentMediaIndex, false);
  };

  onOptionClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    if (button.disabled) return;

    const group = button.closest('[data-option-group]');
    if (!(group instanceof HTMLElement)) return;

    group.querySelectorAll('[data-option-button]').forEach((item) => {
      item.toggleAttribute('data-selected', item === button);
    });

    const label = this.querySelector(`[data-option-label="${group.dataset.optionPosition}"]`);
    if (label) {
      label.textContent = button.dataset.optionValue || '';
    }

    this.updateVariant();
  };

  updateVariant(renderGallery = true) {
    if (!this.variantData.length || !this.variantInput) return;

    const sortedOptionGroups = this.optionGroups
      .sort((a, b) => Number(a.dataset.optionPosition || 0) - Number(b.dataset.optionPosition || 0))
    const selectedOptions = sortedOptionGroups.map((group) => (
      group.querySelector('[data-option-button][data-selected]')?.dataset.optionValue || ''
    ));

    this.updateOptionAvailability(selectedOptions);

    const exactSelectedVariant = this.variantData.find((variant) => (
      variant.options.length === selectedOptions.length
      && variant.options.every((option, index) => option === selectedOptions[index])
    ));
    const selectedVariant = exactSelectedVariant || (
      selectedOptions.length ? null : this.variantData.find((variant) => String(variant.id) === String(this.variantInput.value))
    );

    if (!selectedVariant) {
      this.setAddButtonState(false);
      this.updateDescription('');
      return;
    }

    this.variantInput.value = selectedVariant.id;
    if (this.priceNode && selectedVariant.price) {
      this.updatePrice(selectedVariant);
    }
    this.updateDescription(selectedVariant.description);
    this.setAddButtonState(selectedVariant.available);
    if (renderGallery) {
      this.renderGallery(selectedVariant.variantImages);
    }
  }

  updateOptionAvailability(selectedOptions = []) {
    if (!this.variantData.length || !this.optionGroups.length) return;

    this.optionGroups.forEach((group) => {
      const optionIndex = Math.max(0, Number(group.dataset.optionPosition || 1) - 1);

      group.querySelectorAll('[data-option-button]').forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;

        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = button.dataset.optionValue || '';
        const hasAvailableVariant = this.variantData.some((variant) => (
          variant.available
          && variant.options.length === candidateOptions.length
          && variant.options.every((option, index) => option === candidateOptions[index])
        ));

        button.toggleAttribute('data-unavailable', !hasAvailableVariant);
        button.toggleAttribute('disabled', !hasAvailableVariant);
        button.setAttribute('aria-disabled', hasAvailableVariant ? 'false' : 'true');
      });
    });
  }

  updatePrice(variant) {
    const priceValue = this.priceNode.querySelector('[data-product-price-value]');
    const compareNode = this.priceNode.querySelector('[data-product-compare-price]');
    const compareValue = this.priceNode.querySelector('[data-product-compare-price-value]');

    if (priceValue) {
      priceValue.textContent = variant.price;
    } else {
      this.priceNode.textContent = variant.price;
    }

    if (compareNode && compareValue) {
      compareValue.textContent = variant.comparePrice || '';
      compareNode.hidden = !variant.comparePrice;
    }
  }

  updateDescription(descriptionHtml) {
    if (!this.descriptionNode) return;

    const descriptionMarkup = typeof descriptionHtml === 'string' ? descriptionHtml.trim() : '';
    this.descriptionNode.innerHTML = descriptionMarkup;
    this.descriptionNode.hidden = descriptionMarkup === '';
  }

  setAddButtonState(isAvailable) {
    if (!this.addButton) return;

    const addLabel = this.addButton.dataset.addLabel || 'EKLE';
    const soldOutLabel = this.addButton.dataset.soldOutLabel || 'TÜKENDİ';

    this.addButton.toggleAttribute('disabled', !isAvailable);
    this.addButton.textContent = isAvailable ? addLabel : soldOutLabel;
  }

  onDrawerTrigger = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const key = trigger.dataset.productDrawerTrigger;
    const template = this.querySelector(`[data-drawer-template="${key}"]`);
    if (!(template instanceof HTMLTemplateElement) || !this.drawer || !this.drawerTitle || !this.drawerContent) return;

    this.drawerTitle.textContent = trigger.dataset.drawerTitle || trigger.textContent?.trim() || '';
    this.drawerContent.innerHTML = template.innerHTML;
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ananas-product-drawer-open');
    this.drawer.querySelector('[data-product-drawer-close]')?.focus();
  };

  closeDrawer = () => {
    if (!this.drawer) return;

    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ananas-product-drawer-open');
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      this.closeLightbox();
      this.closeDrawer();
    }
  };
}

if (!customElements.get('ananas-product')) {
  customElements.define('ananas-product', AnanasProduct);
}
